/* eslint-disable @typescript-eslint/no-explicit-any */
import { commandParameterm } from '@dataTypes/index'
import { audioAPI } from '../services/api'
import { ToothFeature } from '@dataTypes/audio.types'

const isNumber = (num: number | string | null | undefined): boolean => {
  return typeof num === 'number'
}

const parseJSON = (string: string): any => {
  try {
    const result = string
      .replace(/'/g, '"') // Replace all single quotes with double quotes
      .replace(/`/g, '"') // Replace backticks with double quotes
      .replace(/True/g, 'true') // Replace Python-style booleans
      .replace(/False/g, 'false')
    return JSON.parse(result)
  } catch (e) {
    console.error(e)
    return false
  }
}
export function findOverlapWithTruncation(
  sentence1: string,
  sentence2: string,
): { matchingWords: string[]; isTruncated: boolean } {
  const words1 = sentence1.split(' ')
  const words2 = sentence2.split(' ')

  let maxOverlap = 2
  let overlapStart = 0

  for (let i = 0; i < words2.length; i++) {
    let overlap = 0
    for (let j = 0; j < Math.min(words1.length, words2.length - i); j++) {
      if (words1[j] === words2[i + j]) {
        overlap++
      } else {
        break
      }
    }
    if (overlap >= maxOverlap) {
      maxOverlap = overlap
      overlapStart = i
    }
  }

  const matchingWords = words2.slice(overlapStart, overlapStart + maxOverlap)
  const isTruncated = overlapStart > 0

  return { matchingWords, isTruncated }
}

export function adjustToothNumber(
  toothNumber: number,
  position: string,
): { adjustedtoothnum: number; adjustedposition: string } {
  if (toothNumber >= 1 && toothNumber < 16 && position == 'facial') {
    return { adjustedtoothnum: toothNumber + 1, adjustedposition: 'facial' }
  } else if (toothNumber == 16 && position == 'facial') {
    return { adjustedtoothnum: toothNumber, adjustedposition: 'lingual' }
  } else if (toothNumber <= 16 && toothNumber > 1 && position == 'lingual') {
    return { adjustedtoothnum: toothNumber - 1, adjustedposition: 'lingual' }
  } else if (toothNumber == 1 && position == 'lingual') {
    return { adjustedtoothnum: 32, adjustedposition: 'facial' }
  } else if (toothNumber <= 32 && toothNumber > 17 && position == 'facial') {
    return { adjustedtoothnum: toothNumber - 1, adjustedposition: 'facial' }
  } else if (toothNumber == 17 && position == 'facial') {
    return { adjustedtoothnum: toothNumber, adjustedposition: 'lingual' }
  } else if (toothNumber < 32 && toothNumber >= 17 && position == 'lingual') {
    return { adjustedtoothnum: toothNumber + 1, adjustedposition: 'lingual' }
  }
  return { adjustedtoothnum: toothNumber, adjustedposition: position }
}

function valueExtractor(
  distal: number,
  buccal: number,
  mesial: number,
  dentalobject: any,
): any {
  const result: any = {}
  result.D = distal
  result.B = buccal
  result.M = mesial
  return Object.keys(result).length ? result : null
}

function valueMFExtractor(
  updateType: string,
  value: number,
  dentalobject: any,
): any {
  const result: any = {}
  if (updateType == 'furcation') {
    return value
  } else if (updateType == 'mobility') {
    return value
  }
}
export function returnUpdatedValues(d: any, toothfeature: any): any {
  const uniquearray = Array.from(new Set(toothfeature.update))
  let updates = {}
  const ulength = uniquearray.length
  for (let i = 0; i < ulength; i++) {
    const ud = uniquearray[i]
    let buffer = {}
    if (ud == 'pocket depth') {
      const innervalues = valueExtractor(
        toothfeature.pd_distal,
        toothfeature.pd_buccal,
        toothfeature.pd_mesial,
        d.PD,
      )
      buffer = innervalues ? { PD: innervalues } : {}
    } else if (ud == 'gingival margin') {
      const innervalues = valueExtractor(
        toothfeature.gm_distal,
        toothfeature.gm_buccal,
        toothfeature.gm_mesial,
        d.GM,
      )
      buffer = innervalues ? { GM: innervalues } : {}
    } else if (ud == 'calculus') {
      const innervalues = valueExtractor(
        toothfeature.ca_distal,
        toothfeature.ca_buccal,
        toothfeature.ca_mesial,
        d.calculus,
      )
      buffer = innervalues ? { calculus: innervalues } : {}
    } else if (ud == 'bleeding') {
      const innervalues = valueExtractor(
        toothfeature.bl_distal,
        toothfeature.bl_buccal,
        toothfeature.bl_mesial,
        d.bleeding,
      )
      buffer = innervalues ? { bleeding: innervalues } : {}
    } else if (ud == 'plaque') {
      const innervalues = valueExtractor(
        toothfeature.pl_distal,
        toothfeature.pl_buccal,
        toothfeature.pl_mesial,
        d.plaque,
      )
      buffer = innervalues ? { plaque: innervalues } : {}
    } else if (ud == 'suppuration') {
      const innervalues = valueExtractor(
        toothfeature.su_distal,
        toothfeature.su_buccal,
        toothfeature.su_mesial,
        d.suppuration,
      )
      buffer = innervalues ? { suppuration: innervalues } : {}
    } else if (ud == 'furcation') {
      const innervalues = valueMFExtractor(
        'furcation',
        toothfeature.furcation,
        d,
      )
      buffer = innervalues ? { furcation: innervalues } : {}
    } else if (ud == 'mobility') {
      const innervalues = valueMFExtractor('mobility', toothfeature.mobility, d)
      buffer = innervalues ? { mobility: innervalues } : {}
    } else {
      buffer = {}
    }
    updates = { ...updates, ...buffer }
  }
  return updates
}

export function returnUpdate(
  d: any,
  updatedProperty: any,
  toothnumber: number,
  position: string,
  toothfeature: any,
): any {
  /*
    Convert values in tobleviewformat
    Args:
        d                (any): Tableview item in object format
        updatedProperty  (any): Latest updates to targeted tooth in tableview
        toothnumber   (number): Target Tooth number 
        position      (string): Position type ( 'facial' | 'lingual' )
        toothfeature     (any): Object to update targeted tooth 
    Returns:
        Obj:  an updated version of tableview item
  */
  if (d.toothNumber === toothnumber + '' && position === 'facial') {
    const updates = returnUpdatedValues(d.toothA, toothfeature)
    updatedProperty = { ...updatedProperty, ...updates }
    return {
      ...d,
      toothA: {
        ...d.toothA,
        ...updatedProperty,
      },
    }
  } else if (d.toothNumber === toothnumber + '' && position === 'lingual') {
    const updates = returnUpdatedValues(d.toothAB, toothfeature)
    updatedProperty = { ...updatedProperty, ...updates }
    return {
      ...d,
      toothAB: {
        ...d.toothAB,
        ...updatedProperty,
      },
    }
  } else if (d.toothNumber === toothnumber + '') {
    const updates = returnUpdatedValues(d, toothfeature)
    updatedProperty = { ...updatedProperty, ...updates }
    return {
      ...d,
      toothA: {
        ...d.toothA,
        ...updatedProperty,
      },
    }
  } else {
    return {
      ...d,
    }
  }
}

export const generateRandomString = (length: number): string => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const charactersLength = characters.length

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }

  return result
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function extract(
  key: string,
  value: string[] | string | number | null,
  body: commandParameterm,
  mode: string,
): boolean {
  /*
    Check if Information is Extractable, Updates tooth position if possible
    Args:
      key                                (string): Name of toothfeature  
      value   (string[] | string | number | null): Value paired witth the key
      body                    (commandParameterm): Object with stored dispatch functions
      mode                               (string): Indicator to check if free or keyword mode     
    Returrns:
      boolean: A boolean to check if information is extractable
  */
  const value_update = {
    update: ['no update'],
    pd_mesial: 0,
    pd_buccal: 0,
    pd_distal: 0,
    gm_mesial: 0,
    gm_buccal: 0,
    gm_distal: 0,
    bl_mesial: 0,
    bl_buccal: 0,
    bl_distal: 0,
    ca_mesial: 0,
    ca_buccal: 0,
    ca_distal: 0,
    pl_mesial: 0,
    pl_buccal: 0,
    pl_distal: 0,
    su_mesial: 0,
    su_buccal: 0,
    su_distal: 0,
    furcation: 0,
    mobility: 0,
  }
  if (key == 'tooth_number' && !Array.isArray(value) && value) {
    if (isNumber(value)) {
      body.setToothFeature((prev: any) => {
        return {
          ...prev,
          ...value_update,
        }
      })
      body.toothNumber.current = value as number
      body.setToothNumber(value as number)
      return true
    } else {
      return false
    }
  } else if (key.includes('position') && !Array.isArray(value) && value) {
    body.position.current = value as string
    body.setPosition(value as string)
    return true
  } else if (
    ['facial_mentioned', 'lingual_mentioned'].includes(key) &&
    !Array.isArray(value) &&
    value
  ) {
    let pos = body.position.current
    if (key.includes('facial')) {
      pos = 'facial'
    } else if (key.includes('lingual')) {
      pos = 'lingual'
    }
    body.position.current = pos
    body.setPosition(pos)
    return true
  } else if (key.includes('clear')) {
    let updatekey = ''
    if (key.includes('pocket') || key.includes('gingival')) {
      updatekey = key.includes('pocket') ? 'pocket depth' : 'gingival margin'
    } else if (
      key.includes('bleeding') ||
      key.includes('plaque') ||
      key.includes('suppuration')
    ) {
      updatekey = key.includes('bleeding')
        ? 'bleeding'
        : key.includes('plaque')
          ? 'plaque'
          : key.includes('suppuration')
            ? 'suppuration'
            : ''
    } else if (key.includes('furcation') || key.includes('mobility')) {
      updatekey = key.includes('furcation')
        ? 'furcation'
        : key.includes('mobility')
          ? 'mobility'
          : ''
    }

    value_update.update = [updatekey]
    body.setToothFeature((prev: any) => {
      return {
        ...prev,
        ...value_update,
      }
    })
  } else if (
    ['number_sequence', 'bleeding', 'plaque', 'suppuration'].includes(key) &&
    !Array.isArray(value) &&
    value
  ) {
    return true
  } else if (
    ['mesial_bleeding', 'distal_bleeding', 'buccal_bleeding'].includes(key) &&
    !Array.isArray(value) &&
    value
  ) {
    const nvalue = value ? 1 : 0
    if (isNumber(nvalue)) {
      return true
    }
    return false
  } else if (
    ['mesial_calculus', 'buccal_calculus', 'distal_calculus'].includes(key) &&
    !Array.isArray(value) &&
    value
  ) {
    const nvalue = value ? 1 : 0
    if (isNumber(nvalue)) {
      return true
    }
    return false
  } else if (
    ['mesial_plaque', 'buccal_plaque', 'distal_plaque'].includes(key) &&
    !Array.isArray(value) &&
    value
  ) {
    const nvalue = value ? 1 : 0
    if (isNumber(nvalue)) {
      return true
    }
    return false
  } else if (
    ['mesial_suppuration', 'buccal_suppuration', 'distal_suppuration'].includes(
      key,
    ) &&
    !Array.isArray(value) &&
    value
  ) {
    const nvalue = value ? 1 : 0
    if (isNumber(nvalue)) {
      return true
    }
    return false
  } else if (key == 'furcation' && !Array.isArray(value)) {
    if (isNumber(value)) {
      return true
    } else if (mode === 'keywords') {
      return true
    }
    return false
  } else if (key == 'mobility' && !Array.isArray(value)) {
    if (isNumber(value)) {
      return true
    } else if (mode === 'keywords') {
      return true
    }
    return false
  } else if (
    [
      'mesial_pocket_depth',
      'buccal_pocket_depth',
      'distal_pocket_depth',
    ].includes(key) &&
    !Array.isArray(value)
  ) {
    if (isNumber(value)) {
      return true
    }
    return false
  } else if (
    [
      'mesial_gingival_margin',
      'buccal_gingival_margin',
      'distal_gingival_margin',
    ].includes(key) &&
    !Array.isArray(value)
  ) {
    if (isNumber(value)) {
      return true
    }
    return false
  } else {
    return false
  }
  return false
}

const hasKeyword = (obj: any, keywords: string[] | string): boolean => {
  /*
  Return values for keywords from an object or nested object
  Args:
    obj                (obj): An object with key-value or key-nextobject values
    keywords: (String[]|str): Keywords to find inside the obj
  Returns:
    Boolean: Check if keyword exists within the object or the nested object
  */
  // Make sure keywords is a string array
  // Make sure keywords is a string array
  const keywordList = Array.isArray(keywords) ? keywords : [keywords]
  // Check if value exists
  return Object.entries(obj).some(([key, value]) => {
    // Check if the value exisit within the object
    const keyMatches = keywordList.some((word) => key.includes(word))
    // Check if the value exists withn the nested object
    const nestedMatches =
      value && typeof value === 'object' && hasKeyword(value, keywordList)

    return keyMatches || nestedMatches
  })
}

const getValueForKeyword = (obj: any, keywords: string[] | string): any => {
  /*
  Return values for keywords from an object or nested object
  Args:
    obj                (obj): An object with key-value or key-nextobject values
    keywords: (String[]|str): Key used to exrtact value from obj
  Returns:
    any: Value corresponding to keywords
  */
  // Make sure keywords is a string array
  const keywordList = Array.isArray(keywords) ? keywords : [keywords]
  //loop through the object to extract value based on key word
  for (const [key, value] of Object.entries(obj)) {
    if (keywordList.some((word) => key.includes(word))) {
      return value
    }
    if (value && typeof value === 'object') {
      // repeat process if value is a nested object
      const nestedResult = getValueForKeyword(value, keywordList)
      if (nestedResult !== undefined) {
        return nestedResult
      }
    }
  }

  return undefined
}

function updateFromSubkeys(
  mainkey: string,
  subvalue: any,
  prefix: string,
  value: any,
  currentValueUpdate: Record<string, any>,
): Record<string, any> {
  /*
  Extracts values from subkeys for toothfeature updates
  Args:
    mainkey    (string): Name of features ('bleading' | 'plaque' | 'suppuration' | 'gingival_margin' | 'pocket_depth')
    subvalue   (string): Object or record as nested object corresponding to mainkey
    prefix     (string): Prefix for features (bl' | 'pl' |'su'| 'gm' |'pd')
    value      (any): String, boolean or number: value corresponding to trarget key in subvalue
  Returns:
    Object:    Object of key-value pairs for tooth features update 
  */
  // Get all possible keys from subvalue
  const subkeys = Object.keys(subvalue)
  let updated = { ...currentValueUpdate }
  updated = { ...updated }
  // Loop through the keys
  for (let j = 0; j < subkeys.length; j++) {
    // Extract data to store to updated var
    if (
      [`mesial_${mainkey}`, `buccal_${mainkey}`, `distal_${mainkey}`].includes(
        subkeys[j],
      )
    ) {
      const part = subkeys[j].includes('mesial')
        ? 'mesial'
        : subkeys[j].includes('buccal')
          ? 'buccal'
          : 'distal'
      const key = `${prefix}_${part}`
      updated[key] = value ? 1 : 0
    }
  }
  // return object with key-value pair to update the tooth feature
  return updated
}

function updateFromFreeMode(
  keyStr: string,
  prefix: string,
  value: any,
  currentValueUpdate: Record<string, any>,
): Record<string, any> {
  /*
  This is used to update toothfeatures in free mode
  Args:
    keyStr                             (string): Tooth location sufix (mesial | distal | buccal) 
    prefix                             (string): Feature name prefix ('bl', 'su' 'pl')
    value                                 (any): A string, number or boolean releated to toothfeatures 
    currentValueUpdate:   (Record<string, any>): A record/object to key-value pairs for tooth features 
  Returns: 
    Objectt:   Object of key-value pairs for tooth features update
  */
  const lowerKey = keyStr.toLowerCase()

  const parts = ['mesial', 'buccal', 'distal']
  let updated = { ...currentValueUpdate }
  updated = { ...updated }
  for (const part of parts) {
    if (lowerKey.includes(part)) {
      const key = `${prefix}_${part}`
      updated[key] = value ? 1 : 0
    }
  }

  return updated
}

const iterateValues = (
  keys: string[],
  values: any,
  body: commandParameterm,
  update: string[],
  type: string,
  valueupdate: any,
  length: number,
  mode: string,
): any => {
  /*
  Extracts feature values per transcript 
  Args:
    keys                         (string[]): List of keys found in the transcript
    values   (obj| string| number| boolean): An object, string, number, boolean or list of values per key 
    body                (commandParameterm): Values inside commandParametem
    update                       (string[]): An empty string to store keywords on what to update
    type                           (string): Number sequence type either (pocket depth or gingival margin)
    valueupdate                       (obj): An empty object to store keywords and values to reflex changes on toothfeatures per tooth
    length                         (number): Number of key in keys
    mode                           (string):  Mode for extraction either (free or keywords)
  */

  // Dynamically write key base on type
  const getKey = (base: string, area: string): string =>
    base === 'pocket_depth' ? `pd_${area}` : `gm_${area}`

  // Loop through the keys
  for (let i = 0; i < length; i++) {
    // Get key
    const key = keys[i]
    // Get values
    const val = values[key]
    // Check for valid key values
    if (!extract(key, val, body, mode)) continue
    // Check for number sequences
    if (key.includes('number_sequence')) {
      // Append keyword for number sequence type
      update.push(type === 'pocket_depth' ? 'pocket depth' : 'gingival margin')
      // Loop throug object to get values for mesial, buccal and distal
      for (const sub of Object.keys(val)) {
        valueupdate[getKey(type, sub)] = val[sub]
      }
      // Check for pocket_depth or gingival margin
    } else if (
      key.includes('pocket_depth') ||
      key.includes('gingival_margin')
    ) {
      // Get prefix
      const prefix = key.includes('pocket_depth')
        ? 'pocket depth'
        : 'gingival margin'
      // suffix
      const side = key.includes('mesial')
        ? 'mesial'
        : key.includes('buccal')
          ? 'buccal'
          : 'distal'
      // update keywords
      update.push(prefix)
      // update changes with keys and values
      valueupdate[getKey(prefix.replace(' ', '_'), side)] = val
    } else if (
      ['bleeding', 'calculus', 'plaque', 'suppuration'].some((k) =>
        key.includes(k),
      )
    ) {
      // Define prefix per posible keywords
      const typeMap: any = {
        bleeding: 'bl',
        calculus: 'ca',
        plaque: 'pl',
        suppuration: 'su',
      }
      // Get keyword by key
      const label = Object.keys(typeMap).find((k) => key.includes(k))!
      // Update keyword
      update.push(label)
      // Get prefix
      const short = typeMap[label]
      // Update changes with keys and values
      valueupdate =
        mode === 'free'
          ? updateFromFreeMode(key, short, val, valueupdate)
          : updateFromSubkeys(key, val, short, val, valueupdate)
    } else if (key.includes('furcation') || key.includes('mobility')) {
      const label = key.includes('furcation') ? 'furcation' : 'mobility'
      update.push(label)
      valueupdate[label] = mode === 'free' ? val : val[label]
    }
  }
  // return values for toothfeatures
  return { v: valueupdate, u: update }
}

const updatefreemode = async (
  det1: any,
  body: commandParameterm,
): Promise<any> => {
  /*
  Asynchronously extract and update chart based on data from voice assistant api 
  Args:
    det1 (object): An object containing object returned by voice assistant api 
    body (commandParameter): Object that includes audio data file name and others
  Returns:
    void 
  */
  let ispause = false
  let resume = false
  let isnext = false
  let isback = false

  const transcripts = det1['transcripts']
  const trans_length = transcripts.length
  // Loop through all transcripts
  for (let j = 0; j < trans_length; j++) {
    const update: string[] = []
    //Extract individual transcripts
    const transcript = transcripts[j]
    //Get all keys excluding: position, tootnumber, facial mention
    const buffkeys = Array.from(new Set(Object.keys(transcript))).filter(
      (d) =>
        d != 'position' &&
        d != 'tooth_number' &&
        d != 'facial_mentioned' &&
        d != 'lingual_mentioned',
    )
    // Get all keys mentioned
    const keys = [
      'tooth_number',
      'facial_mentioned',
      'lingual_mentioned',
      ...buffkeys,
    ]
    //Get tooth number per transcript
    body.currenttooth.current.number = hasKeyword(transcript, 'tooth_number')
      ? transcript['tooth_number']
      : body.currenttooth.current.number
    const length = keys.length
    // Get feature values per tooth
    const { v, u } = iterateValues(
      keys,
      transcript,
      body,
      update,
      '',
      {},
      length,
      'free',
    )
    // Update toothfeatures
    if (u.length > 0) {
      body.setToothFeature((prev: any) => {
        return { ...prev, update: u as string[], ...v }
      })
      await sleep(500)
    }
  }
  // Check for pause, resume, next and back commands
  ispause = hasKeyword(transcripts[trans_length - 1], 'pause_mentioned')
  resume = hasKeyword(transcripts[trans_length - 1], 'resume_mentioned')
  isnext = hasKeyword(transcripts[trans_length - 1], 'next_mentioned')
  isback = hasKeyword(transcripts[trans_length - 1], 'back_mentioned')

  // Check for next or back commands invoked
  if (isnext || isback) {
    // Calculate targeted tooth number
    const update = isnext
      ? Math.min(...[body.currenttooth.current.number + 1, 32])
      : Math.max(...[body.currenttooth.current.number - 1, 1])
    body.currenttooth.current.number = update
    // Update Chart
    extract('tooth_number', body.currenttooth.current.number, body, 'free')
  }
  // Check pause or resume command
  if (ispause || resume) {
    //Invoke command by changing pause indicator on current tooth ref
    body.currenttooth.current.pause = ispause
  }
}

const updatekeymode = async (
  det1: any,
  body: commandParameterm,
): Promise<any> => {
  /*
  Asynchronously extract and update chart based on data from voice assistant api 
  Args:
    det1 (object): An object containing object returned by voice assistant api 
    body (commandParameter): Object that includes audio data file name and others
  Returns:
    void 
  */
  //Get all keys excluding: position, tootnumber, facial mention
  const buffkeys = Array.from(
    new Set(Object.keys(det1['transcripts'][0])),
  ).filter(
    (d) =>
      d != 'position' &&
      d != 'tooth_number' &&
      d != 'facial_mentioned' &&
      d != 'lingual_mentioned',
  )
  // Record currnt tooth number where chart is focused
  const currenttoothnumber = body.currenttooth.current.number
  // Get the current position of tooth which is either ('facial'/'lingual)
  let position = body.currenttooth.current.position
  // Get the current number sequence type of tooth which is either ('pocket depth'/'gingival margin)
  let type = body.currenttooth.current.type
  // Initialize an empty array for storing expected tooth number
  const tooth_numbers = []
  // Get the first and only transcript
  const transcript = det1['transcripts'][0]
  // Get all keys mentioned
  const keys = ['tooth_number', ...buffkeys]
  // Check if it's a jump command
  const jump = hasKeyword(transcript, 'is_jump')
  // Check if it's a repeat command
  const repeat = hasKeyword(transcript, 'is_repeat')
  // Check if it's a next command
  const isnext = hasKeyword(transcript, 'next_mentioned')
  // Check if it's a back command
  const isback = hasKeyword(transcript, 'back_mentioned')
  // Check if it's an undo command
  const isundo = hasKeyword(transcript, 'undo_mentioned')
  // Check if it's a pause command
  const ispause = hasKeyword(transcript, 'pause')
  // Check if it's a resume command
  const resume = hasKeyword(transcript, 'resume')
  // Check if it's a number sequence transcript
  const number_sequence = hasKeyword(transcript, 'number_sequence')
  // Check if it's a bleeding transcript
  const bleeding = hasKeyword(transcript, 'bleeding')
  // Check if it's a plaque transcript
  const plaque = hasKeyword(transcript, 'plaque')
  // Check if it's a suppuration transcript
  const suppuration = hasKeyword(transcript, 'suppuration')
  // Check if it's a mobility transcript
  const mobility = hasKeyword(transcript, 'mobility')
  // Check if it's a furcation transcript
  const furcation = hasKeyword(transcript, 'furcation')

  // Check for nested commands
  // Check for quadrant 1 command
  const quadrant1 = hasKeyword(transcript, 'quadrant1')
  // Check for quadrant 2 command
  const quadrant2 = hasKeyword(transcript, 'quadrant2')
  // Check for quadrant 3 command
  const quadrant3 = hasKeyword(transcript, 'quadrant3')
  // Check for quadrant 4 command
  const quadrant4 = hasKeyword(transcript, 'quadrant4')
  // Check for repeat command
  const nest_repeat = hasKeyword(transcript, 'repeat')

  // Check for keywords mentioned
  // Check if type is mentioned
  const istype = hasKeyword(transcript, 'type')
  // Check if tooth number is mentioned
  const istooth = hasKeyword(transcript, 'tooth_number')
  // Check if a tooth feature is mentioned
  const hasothers = [bleeding, plaque, suppuration, mobility, furcation].some(
    (h: boolean) => h,
  )
  // Write the maximum range per quadrant
  const [quadrant1teeth, quadrant2teeth, quadrant3teeth, quadrant4teeth] =
    Array.from({ length: 4 }, (_a, q) =>
      Array.from({ length: 8 }, (_b, i) => q * 8 + i + 1),
    )
  // Check for pause or resume command
  if (ispause || resume) {
    body.currenttooth.current.pause = ispause ? ispause : resume
  }
  // Invoke undo command
  if (isundo) {
    body.setUndo(true)
  }
  // Check for number sequence transcript with repeat command
  if (number_sequence && (repeat || nest_repeat)) {
    // Get number of times to repeat
    const repeatnumber = getValueForKeyword(transcript, 'repeat_number')
    // Get current tooth number
    const tooth = currenttoothnumber
    // Append all callculated tooth numbers to array
    for (let i = 0; i <= repeatnumber; i++) tooth_numbers.push(tooth + i)
    // reassign current tooth number
    body.currenttooth.current.number += repeatnumber
    // Check for number sequence
  } else if (number_sequence) {
    // Add current tooth number to array
    tooth_numbers.push(currenttoothnumber)
    // Move forward once
    body.currenttooth.current.number = Math.min(currenttoothnumber + 1, 32)
    // Check for next and back commands
  } else if (isnext || isback) {
    // Intialize added value
    const addedvalue = isnext
      ? [currenttoothnumber + 1, 32]
      : [currenttoothnumber - 1, 1]
    // Append calculated tooth number
    tooth_numbers.push(
      isnext ? Math.min(...addedvalue) : Math.max(...addedvalue),
    )
    // Assign calculated tooth number to current tooth number
    body.currenttooth.current.number = isnext
      ? Math.min(...addedvalue)
      : Math.max(...addedvalue)
    // Check for jump commands
  } else if (jump && (istooth || istype)) {
    // Calculate tooth number
    const jumpTooth = istooth
      ? transcript.jump.tooth_number
      : currenttoothnumber
    // Append calculated tooth number
    tooth_numbers.push(jumpTooth)

    // If tooth is mentioned then apply changes to position and tooth
    if (istooth) {
      position = transcript.jump.position[0]
      body.currenttooth.current.position = position
      body.currenttooth.current.number = jumpTooth
    }
    // If type is mentioned then apply changes to type
    if (istype) {
      type = getValueForKeyword(transcript, 'type')
      body.currenttooth.current.type = type
    }
    // If tooth is mentioned with repeat command
  } else if (istooth && (repeat || nest_repeat)) {
    // Extract number of repeats
    const repeatnumber = getValueForKeyword(transcript, 'repeat_number')
    // Get tooth number from transcript
    const tooth = getValueForKeyword(transcript, 'tooth_number')
    // Append computed tooth numbers to array
    for (let i = 0; i <= repeatnumber; i++) tooth_numbers.push(tooth + i)
    // Check if features are not mentioned
    if (!hasothers) {
      // use last computed tooth as current tooth number
      body.currenttooth.current.number = tooth + repeatnumber
    }
    // Check if tooth is mentioned
  } else if (istooth) {
    // Append computed tooth number to array
    tooth_numbers.push(getValueForKeyword(transcript, 'tooth_number'))
  }
  // consolidate all data related to quadrants
  const quadrants = [
    { flag: quadrant1, teeth: quadrant1teeth, last: 8 },
    { flag: quadrant2, teeth: quadrant2teeth, last: 16 },
    { flag: quadrant3, teeth: quadrant3teeth, last: 24 },
    { flag: quadrant4, teeth: quadrant4teeth, last: 32 },
  ]
  // Check if one of the quadrants is mentioned
  const active = quadrants.find((q: any) => q.flag)
  // f Active
  if (active) {
    // All tooth numbers that are part of the quadrant are pushed to the array
    tooth_numbers.push(...active.teeth)
    //If features are not mentioned then assign last quadrant tooth number to current
    if (!hasothers) body.currenttooth.current.number = active.last
  }
  const toothlength = tooth_numbers.length
  // Loop through all tooth numbers in the array
  for (let j = 0; j < toothlength; j++) {
    const update: string[] = []
    extract('tooth_number', tooth_numbers[j], body, 'keywords')
    extract('position', position, body, 'keywords')
    const length = keys.length
    // Get feature values per tooth
    const { v, u } = iterateValues(
      keys,
      transcript,
      body,
      update,
      type,
      {},
      length,
      'keywords',
    )
    // Update toothfeatures
    if (u.length > 0) {
      body.setToothFeature((prev: any) => {
        return { ...prev, update: u as string[], ...v }
      })
      await sleep(500)
    }
  }
  //return tooth highlight to current tooth number
  body.toothNumber.current = body.currenttooth.current.number
  extract('tooth_number', body.currenttooth.current.number, body, 'keywords')
  await sleep(500)
}

export const sendAudioCommandkm = async (
  body: commandParameterm,
): Promise<any> => {
  /*
  Asynchronously call on api to extract dental data from audio files  in keywordmode 
  Args:
    body (commandParameter): Object that includes audio data file name and others
  Returns:
    object: data extracted from audio files 
  */
  // Extract audio data
  const audioBlob1 = body.data
  // Create formdata
  const formdata = new FormData()
  formdata.append('audiofile', audioBlob1, body.filename + '.wav')
  formdata.append('isfirst', body.isFirst + '')
  formdata.append('stream', body.stream + '')
  formdata.append('pause', body.currenttooth.current.pause + '')
  // Get raw extracted data from api
  const response = await audioAPI.sendAudioCommandm(
    formdata,
    body.session_id.current,
    body.type,
  )
  let { result1 } = response
  result1 = result1.replace('"{', '{').replace('}"', '}')
  // Parse json string
  const res1 = parseJSON(result1)
  const det1 = res1.details || {
    transcripts: [],
  }
  // Update targeted teeth with tooth features
  await updatekeymode(det1, body)
  // Return raw response
  return response
}

export const sendAudioCommandfm = async (
  body: commandParameterm,
): Promise<any> => {
  /*
  Asynchronously call on api to extract dental data from audio files in freemode 
  Args:
    body (commandParameter): Object that includes audio data file name and others
  Returns:
    object: data extracted from audio files 
  */

  // Extract audio data
  const audioBlob1 = body.data
  // Create formdata
  const formdata = new FormData()
  formdata.append('audiofile', audioBlob1, body.filename + '.wav')
  formdata.append('isfirst', body.isFirst + '')
  formdata.append('stream', body.stream + '')
  // Get raw extracted data from api
  const response = await audioAPI.sendAudioCommandm(
    formdata,
    body.session_id.current,
    body.type,
  )
  // Extract result
  let { result1 } = response
  result1 = result1.replace('"{', '{').replace('}"', '}')
  // Parse json string
  const res1 = parseJSON(result1)
  const det1 = res1.details || {
    transcripts: [],
  }
  // Update targeted teeth with tooth features
  await updatefreemode(det1, body)
  // Return raw response
  return response
}

export const buildTotalDataset = (
  teethSet1TableView: any[],
  teethSet2TableView: any[],
  setTotalDataset: React.Dispatch<React.SetStateAction<any[]>>,
): void => {
  /* 
  Update chart if there are changes with teethSet1TableView or teethSet2TableView or both
  Args:
    teethSet1TableView                                  (any[]): An array to store all feature changes on upper  tooth per tooth number  (react state)
    teethSet2TableView                                  (any[]): An array to store all feature changes on bottom tooth per tooth number  (react state)
    setTotalDataset     (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update totalDataset
  Returns:
    void  
  */
  // Initialize varx array
  const varx = []
  // Check if teethSet1TableView is not empty
  if (teethSet1TableView.length > 0) {
    varx.push(...teethSet1TableView)
  }
  // Check if teethSet2TableView is not empty
  if (teethSet2TableView.length > 0) {
    varx.push(...teethSet2TableView)
  }
  // Update totalDataset to update chart
  setTotalDataset([...varx])
}

export const handleToothHighlight = (
  toothnumber: number,
  position: string,
  toothref: React.MutableRefObject<number>,
  positionref: React.MutableRefObject<string>,
  toothChange: React.MutableRefObject<boolean>,
  teethSet1TableView: any[],
  setTeethSet1TableView: React.Dispatch<React.SetStateAction<any[]>>,
  teethSet2TableView: any[],
  setTeethSet2TableView: React.Dispatch<React.SetStateAction<any[]>>,
): void => {
  /*
  Tiggers the chart UI to focus on targeted tooth position
  Args:
    toothnumber                                           (number): An integer to indicate the targeted tooth position (react state)
    position:                                             (string): A string to indicate whether position is (facial/lingual)  (react state)
    toothref                            (MutableRefObject<number>): An integer to store and reflect realtime changes to tooth number
    positionref                         (MutableRefObject<string>): A string to store and reflect realtime changes to tooth position
    toothChange                        (MutableRefObject<boolean>): A boolean to store and reflect realtime isolated change to tooth number 
    teethSet1TableView:                                    (any[]): An array to store all feature changes on upper tooth per tooth number  (react state)
    setTeethSet1TableView: (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet1TableView
    teethSet2TableView:                                    (any[]): An array to store all feature changes on bottom tooth per tooth number  (react state)
    setTeethSet2TableView: (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet2TableView
  Returns:
    void    
  */

  // Change tooth number's ref object to reflect changes realtime
  toothref.current = toothnumber
  // Change tooth position's ref object to reflect changes realtime
  positionref.current = position
  // Change toothChange's ref object to reflect changes are isoloted in tooth position only
  toothChange.current = true

  const updateTableView = (
    data: any[],
    setData: React.Dispatch<React.SetStateAction<any[]>>,
    range: [number, number],
  ): void => {
    /*
      Updates teethSet1TableView or teethSet2TableView react state
      Args:
        data                                           (any[]): The current array and value for teethSet1TableView  (react state)
        setData: (React.Dispatch<React.SetStateAction<any[]>>): Dispatch function to change state of either teethSet1TableView or teethSet2TableView 
        range:                              ([number, number]): Array containing the maximum and minimum range of tooth number
      Returns:
        void    
    */

    // Check id data is an array and it tooth number is within range
    if (data.length && toothnumber >= range[0] && toothnumber < range[1]) {
      // Update the teethSet1TableView or teethSet2TableView to highlight the targeted tooth position in the chart
      setData((prev: any) =>
        prev.map((d: any) => {
          const isTarget = d.toothNumber === toothnumber + ''
          const highlight = { backgroundColor: '#00FFFF' }
          const clear = { backgroundColor: '#FFFFFF00' }
          return {
            ...d,
            facial: isTarget && position === 'facial' ? highlight : clear,
            lingual: isTarget && position === 'lingual' ? highlight : clear,
          }
        }),
      )
    }
  }
  // Update teethSet1TableView
  updateTableView(teethSet1TableView, setTeethSet1TableView, [0, 16])
  // Update teethSet2TableView
  updateTableView(teethSet2TableView, setTeethSet2TableView, [16, 32])
}

export const resetToothFeature = (
  toothChange: React.MutableRefObject<boolean>,
  setToothFeature: React.Dispatch<React.SetStateAction<ToothFeature>>,
): void => {
  /*
   Change the value of toothfeature to it's reset value. This is used to when changing tooth position in the chart
   Args:
    toothChange:                          (React.MutableRefObject<boolean>): A boolean to store and reflect realtime isolated change to tooth number 
    setToothFeature:   (React.Dispatch<React.SetStateAction<ToothFeature>>): Dispatch function to change state of toothfeature of any tootth 
   Returns:
    void
  */
  toothChange.current = true

  const value_update = {
    update: ['no update'],
    pd_mesial: 0,
    pd_buccal: 0,
    pd_distal: 0,
    gm_mesial: 0,
    gm_buccal: 0,
    gm_distal: 0,
    bl_mesial: 0,
    bl_buccal: 0,
    bl_distal: 0,
    ca_mesial: 0,
    ca_buccal: 0,
    ca_distal: 0,
    pl_mesial: 0,
    pl_buccal: 0,
    pl_distal: 0,
    su_mesial: 0,
    su_buccal: 0,
    su_distal: 0,
    furcation: 0,
    mobility: 0,
  }

  setToothFeature((prev: any) => ({
    ...prev,
    ...value_update,
  }))
}

export const updateToothTableView = (
  toothfeature: ToothFeature,
  toothnumber: number,
  position: string,
  teethSet1TableView: any[],
  setTeethSet1TableView: React.Dispatch<React.SetStateAction<any[]>>,
  teethSet2TableView: any[],
  setTeethSet2TableView: React.Dispatch<React.SetStateAction<any[]>>,
  prevTeethSet1Ref: React.MutableRefObject<any[]>,
  prevTeethSet2Ref: React.MutableRefObject<any[]>,
): void => {
  /*
  Update toothtables for tooth features teethSet1TableView (upper tooth) or teethSet2TableView (bottom tooth) 
  Args:
    toothfeature                                                 (ToothFeature): An object that stores changes in toothfeature (react state) 
    toothnumber                                                        (number): An integer to indicate the targeted tooth position (react state)
    position                                                           (string): A string to indicate whether position is (facial/lingual)  (react state)
    teethSet1TableView:                                                 (any[]): An array to store all feature changes on upper tooth per tooth number  (react state)
    setTeethSet1TableView:              (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet1TableView
    teethSet2TableView:                                                 (any[]): An array to store all feature changes on bottom tooth per tooth number  (react state)
    setTeethSet2TableView:              (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet2TableView
    prevTeethSet1Ref                                  (MutableRefObject<any[]>): An array to store and reflect realtime changes to previous toothtable 
    prevTeethSet2Ref                                  (MutableRefObject<any[]>): An array to store and reflect realtime changes to previous toothtable 
  Returns:
    void
  */

  // Initialize updatedProperty
  let updatedProperty = {}
  updatedProperty = {}
  // Check tooth features for update
  if (!toothfeature.update.includes('no update')) {
    // Store previous data for chart
    prevTeethSet1Ref.current = JSON.parse(JSON.stringify(teethSet1TableView))
    prevTeethSet2Ref.current = JSON.parse(JSON.stringify(teethSet2TableView))
  }

  const updateTeethSet = (
    setFn: React.Dispatch<React.SetStateAction<any[]>>,
  ): void => {
    // Update chart for changes in tooth feature
    setFn((prev: any) =>
      prev.map((d: any) =>
        returnUpdate(d, updatedProperty, toothnumber, position, toothfeature),
      ),
    )
  }
  // Check if toothnumber is in the upper or lower tooth
  if (toothnumber > 0 && toothnumber <= 16 && teethSet1TableView.length) {
    // Update tooth
    updateTeethSet(setTeethSet1TableView)
  } else if (
    toothnumber > 16 &&
    toothnumber <= 32 &&
    teethSet2TableView.length
  ) {
    // Update tooth
    updateTeethSet(setTeethSet2TableView)
  }
}

export const handleUndo = (
  undo: boolean,
  setUndo: React.Dispatch<React.SetStateAction<boolean>>,
  prevTeethSet1Ref: React.MutableRefObject<any[]>,
  prevTeethSet2Ref: React.MutableRefObject<any[]>,
  setTeethSet1TableView: React.Dispatch<React.SetStateAction<any[]>>,
  setTeethSet2TableView: React.Dispatch<React.SetStateAction<any[]>>,
): void => {
  /*
    undo                                                 (boolean): A boolean to indicate if undo function is invoked (react state)
    setUndo              (Dispatch<React.SetStateAction<boolean>>): A dispatch function to update undo
    prevTeethSet1Ref               (React.MutableRefObject<any[]>): An array to store and reflect realtime changes to previous toothtable
    prevTeethSet2Ref               (React.MutableRefObject<any[]>): An array to store and reflect realtime changes to previous toothtable 
    setTeethSet1TableView  (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet1TableView
    setTeethSet2TableView  (Dispatch<React.SetStateAction<any[]>>): A dispatch function to update teethSet2TableView
  */
  // Check if undo command is invoked
  if (undo) {
    // Check if there are previous data for teethSet1TableView
    if (prevTeethSet1Ref.current.length > 0) {
      // Update teethSet1TableView
      setTeethSet1TableView(prevTeethSet1Ref.current)
      // Clear values for prevTeethSet1Ref
      prevTeethSet1Ref.current = []
    }
    // Check if there are previous data for teethSet2TableView
    if (prevTeethSet2Ref.current.length > 0) {
      // Update teethSet2TableView
      setTeethSet2TableView(prevTeethSet2Ref.current)
      // Clear values for prevTeethSet2Ref
      prevTeethSet2Ref.current = []
    }
    setUndo(false)
  }
}
